import type { CasinoMemberView, MemberStatsResponse } from '@tv-casino/shared';
import { profileHex } from '@tv-casino/shared';
import { formatSignedChips } from '../lib/format.js';
import type { CasinoMemberRow } from '@tv-casino/shared';

/**
 * The pure view-model behind the owner's member table: one row per
 * membership with balance, what the house staked, the night's result and a
 * ledger-drift flag. Derived from the REST rows alone — no React, no fetch.
 */

export interface MemberRowVM {
  memberId: string;
  name: string;
  /** Their profile hue, for the dot beside the name. Null when unpicked. */
  accentHex: string | null;
  /** A signed-in account plays under this membership: the profile badge. */
  hasProfile: boolean;
  chipsLabel: string;
  /** "Staked 1,000 · 24 hands" — the line under the name. */
  metaLine: string;
  netLabel: string;
  netTone: 'win' | 'loss' | 'even';
  /**
   * Set when the ledger no longer recomputes to the stored balance. That is
   * a bug, not a state, so the console says so instead of hiding it.
   */
  driftWarning: string | null;
  expanded: boolean;
  statLines: { label: string; value: string }[];
}

export interface MemberTableVM {
  rows: MemberRowVM[];
  /** Shown instead of the rows when nobody has joined the casino yet. */
  emptyLine: string | null;
  totalChipsLabel: string;
  totalNetLabel: string;
}

const chips = (n: number): string => n.toLocaleString();

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${chips(n)} ${n === 1 ? one : many}`;

/** The drill-down grid: the night's totals, then every game's counters. */
export function statLinesOf(stats: MemberStatsResponse): { label: string; value: string }[] {
  const { blackjack, holdem, baccarat, roulette, craps, threecard, keno } = stats.games;
  const totals = stats.totals;
  const lines: { label: string; value: string }[] = [];
  if (totals.rounds > 0) {
    const streak =
      totals.winStreak > 0
        ? `${totals.winStreak} won in a row`
        : totals.lossStreak > 0
          ? `${totals.lossStreak} lost in a row`
          : null;
    lines.push(
      { label: 'Rounds', value: chips(totals.rounds) },
      { label: 'Wagered', value: chips(totals.wagered) },
      { label: 'Returned', value: chips(totals.returned) },
      { label: 'Best round', value: formatSignedChips(totals.bestRound) },
      { label: 'Worst round', value: formatSignedChips(totals.worstRound) },
      ...(streak ? [{ label: 'Streak', value: streak }] : []),
    );
  }
  if (blackjack.hands > 0) {
    lines.push(
      { label: 'BJ hands', value: chips(blackjack.hands) },
      { label: 'Won', value: chips(blackjack.wins) },
      { label: 'Lost', value: chips(blackjack.losses) },
      { label: 'Pushed', value: chips(blackjack.pushes) },
      { label: 'Blackjacks', value: chips(blackjack.blackjacks) },
      { label: 'Doubles', value: chips(blackjack.doubles) },
      { label: 'Splits', value: chips(blackjack.splits) },
      { label: 'Busts', value: chips(blackjack.busts) },
      { label: 'Side bets', value: chips(blackjack.sideBetsPlaced) },
      { label: 'Side wins', value: chips(blackjack.sideBetsWon) },
      { label: 'Best win', value: chips(blackjack.biggestWin) },
      { label: 'BJ net', value: formatSignedChips(blackjack.net) },
    );
  }
  if (holdem.hands > 0) {
    lines.push(
      { label: "Hold'em hands", value: chips(holdem.hands) },
      { label: 'Pots won', value: chips(holdem.potsWon) },
      { label: 'Showdowns', value: chips(holdem.showdowns) },
      { label: 'Folds', value: chips(holdem.folds) },
      { label: 'Voluntary', value: chips(holdem.vpip) },
      { label: 'All-ins', value: chips(holdem.allIns) },
      { label: 'Biggest pot', value: chips(holdem.biggestPot) },
      { label: "Hold'em net", value: formatSignedChips(holdem.net) },
    );
  }
  if (baccarat.hands > 0) {
    lines.push(
      { label: 'Baccarat coups', value: chips(baccarat.hands) },
      { label: 'Won', value: chips(baccarat.wins) },
      { label: 'Lost', value: chips(baccarat.losses) },
      { label: 'Pushed', value: chips(baccarat.pushes) },
      { label: 'Naturals', value: chips(baccarat.naturals) },
      { label: 'Best win', value: chips(baccarat.biggestWin) },
      { label: 'Baccarat net', value: formatSignedChips(baccarat.net) },
    );
  }
  if (roulette.hands > 0) {
    lines.push(
      { label: 'Roulette spins', value: chips(roulette.hands) },
      { label: 'Won', value: chips(roulette.wins) },
      { label: 'Lost', value: chips(roulette.losses) },
      { label: 'Straight hits', value: chips(roulette.straightUpHits) },
      { label: 'Best win', value: chips(roulette.biggestWin) },
      { label: 'Roulette net', value: formatSignedChips(roulette.net) },
    );
  }
  if (craps.hands > 0) {
    lines.push(
      { label: 'Craps hands', value: chips(craps.hands) },
      { label: 'Won', value: chips(craps.wins) },
      { label: 'Lost', value: chips(craps.losses) },
      { label: 'Points made', value: chips(craps.pointsMade) },
      { label: 'Seven outs', value: chips(craps.sevenOuts) },
      { label: 'Rolls thrown', value: chips(craps.rollsShot) },
      { label: 'Best win', value: chips(craps.biggestWin) },
      { label: 'Craps net', value: formatSignedChips(craps.net) },
    );
  }
  if (threecard.hands > 0) {
    lines.push(
      { label: '3-Card hands', value: chips(threecard.hands) },
      { label: 'Won', value: chips(threecard.wins) },
      { label: 'Lost', value: chips(threecard.losses) },
      { label: 'Folds', value: chips(threecard.folds) },
      { label: 'Best win', value: chips(threecard.biggestWin) },
      { label: '3-Card net', value: formatSignedChips(threecard.net) },
    );
  }
  if (keno.hands > 0) {
    lines.push(
      { label: 'Keno draws', value: chips(keno.hands) },
      { label: 'Tickets', value: chips(keno.ticketsPlaced) },
      { label: 'Won', value: chips(keno.wins) },
      { label: 'Lost', value: chips(keno.losses) },
      { label: 'Best catch', value: chips(keno.bestCatch) },
      { label: 'Best win', value: chips(keno.biggestWin) },
      { label: 'Keno net', value: formatSignedChips(keno.net) },
    );
  }
  return lines;
}

export function memberTableModel(
  members: CasinoMemberRow[] | null,
  opts: {
    expandedId?: string | null;
    stats?: MemberStatsResponse | null;
  } = {},
): MemberTableVM {
  const { expandedId = null, stats = null } = opts;
  const rows = (members ?? []).map((member): MemberRowVM => {
    // The house's stakes are not winnings: what the tables moved is the
    // balance minus everything the house handed over.
    const net = member.chips - member.buyIn;
    const expanded = member.id === expandedId;
    return {
      memberId: member.id,
      name: member.name,
      accentHex: member.color ? profileHex(member.color) : null,
      hasProfile: member.hasProfile,
      chipsLabel: chips(member.chips),
      metaLine: `Staked ${chips(member.buyIn)} · ${plural(member.hands, 'hand')}`,
      netLabel: `${formatSignedChips(net)} on the night`,
      netTone: net > 0 ? 'win' : net < 0 ? 'loss' : 'even',
      driftWarning: member.consistent
        ? null
        : 'Ledger and balance disagree — check the chip log.',
      expanded,
      statLines:
        expanded && stats && stats.memberId === member.id ? statLinesOf(stats) : [],
    };
  });

  const totalChips = rows.length === 0 ? 0 : (members ?? []).reduce((sum, m) => sum + m.chips, 0);
  const totalNet =
    rows.length === 0 ? 0 : (members ?? []).reduce((sum, m) => sum + (m.chips - m.buyIn), 0);

  return {
    rows,
    emptyLine:
      members === null
        ? 'Reading the member list…'
        : rows.length === 0
          ? 'Nobody has walked in yet — share the join code.'
          : null,
    totalChipsLabel: `${chips(totalChips)} chips on the floor`,
    totalNetLabel: `${formatSignedChips(totalNet)} against the house`,
  };
}

/**
 * The open drill-down's member card, as `memberMarketModel` reads it: the
 * row's balance and hue with the stats the owner asked for. Null until both
 * are in hand, or when the stats belong to a row that has since closed.
 */
export function deskMemberOf(
  members: readonly CasinoMemberRow[] | null,
  expandedId: string | null,
  stats: MemberStatsResponse | null,
): CasinoMemberView | null {
  if (!expandedId || !stats || stats.memberId !== expandedId || !members) return null;
  const row = members.find((m) => m.id === expandedId);
  if (!row) return null;
  return {
    casinoId: stats.casinoId,
    memberId: stats.memberId,
    name: row.name,
    chips: row.chips,
    buyIn: row.buyIn,
    color: row.color ?? null,
    games: stats.games,
    totals: stats.totals,
  };
}

/** What one press of the grant stepper moves the amount by. */
export const GRANT_STEP = 100;

/**
 * The grant field as the grant route takes it: a whole number of chips, sign
 * left to the Grant / Revoke button. Null when there is nothing to send.
 */
export function grantAmountOf(raw: string): number | null {
  const parsed = Math.abs(Math.round(Number(raw)));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** One stepper press: up or down a step, never below one step. */
export function stepGrantAmount(raw: string, direction: 1 | -1): string {
  const now = grantAmountOf(raw) ?? 0;
  return String(Math.max(GRANT_STEP, now + direction * GRANT_STEP));
}
